import React, { useState, useMemo, useRef, FC } from 'react';
import ky from 'ky';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import {
  Box,
  Text,
  Pagination,
  Button,
  Icon,
  IconSize,
  ButtonAppearance,
  ButtonSize,
  Spinner,
  SpinnerSize,
} from '@bechtel/gds';
import {
  DocumentNavStyled,
  RangeSelectorStyled,
  EmptyDocumentContainerStyled,
  EmptyDocumentContentStyled,
  DocumentSectionStyled,
  DocumentViewStyled,
} from './styled';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as ViewDetailsSvg } from '@bechtel/client-assets/dist/images/view-details.svg';
import { Trans } from '@lingui/macro';

export interface DocumentViewerProps {
  url?: string;
  canDelete?: boolean;
  deleting?: false;
  onDelete?: () => void;
}

export const DocumentViewer: FC<DocumentViewerProps> = ({ url, canDelete, onDelete, deleting }) => {
  const pdfDocumentRef = useRef<HTMLDivElement>(null);
  const printFrameRef = useRef<HTMLIFrameElement>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(25);
  const [loadComplete, setLoadComplete] = useState<boolean>(false);
  const [printing, setPrinting] = useState<boolean>(false);
  const onDocumentLoadSuccess = async (document) => {
    setPage(1);
    setTotalPages(document.numPages);
  };
  //need to memoize for reference equality, else react-pdf Document re-renders on every cycle
  const file = useMemo(() => ({ url: url, withCredentials: true }), [url]);

  const print = async () => {
    if (printing || !url) return;
    setPrinting(true);
    if (!printFrameRef || !printFrameRef.current) return;
    const pdfBlob = await ky
      .get(url, {
        credentials: 'include',
      })
      .blob();
    const blob = new Blob([pdfBlob], { type: 'application/pdf' });
    const objectURL = URL.createObjectURL(blob);
    if (printFrameRef?.current) printFrameRef.current.src = objectURL;
    URL.revokeObjectURL(objectURL);
    setTimeout(function() {
      if (printFrameRef?.current?.contentWindow) printFrameRef.current.contentWindow.print();
      setPrinting(false);
    }, 1000);
  };

  //Determine the scale factor based on pdf document width
  const onPageLoadSuccess = (page) => {
    setLoadComplete(true);
    if (!(pdfDocumentRef && pdfDocumentRef.current)) {
      return;
    }
    const pageScale = (pdfDocumentRef ? pdfDocumentRef?.current?.clientHeight : 0) / page.originalHeight;
    if (scale !== pageScale) {
      //-2 to allow for padding
      setScale(pageScale * 100 - 2);
    }
  };
  return (
    <>
      {url ? (
        <>
          {loadComplete && (
            <DocumentNavStyled>
              <Pagination
                onPrev={() => {
                  setPage((prev) => {
                    if (prev <= 1) {
                      return 1;
                    }

                    return prev - 1;
                  });
                }}
                onNext={() => {
                  setPage((prev) => {
                    if (prev >= totalPages) {
                      return totalPages;
                    }

                    return prev + 1;
                  });
                }}
                currentPage={page.toString()}
                totalPages={totalPages}
                onPageChange={(page) => setPage(+page)}
              />
              <Box flexGrow={{ sm: '2' }} justifyContent={{ sm: 'flex-end' }} mr={{ sm: '500' }}>
                <Box mr={{ sm: '300' }}>
                  <Text>{Math.round(scale)}%</Text>
                </Box>
                <RangeSelectorStyled
                  min="1"
                  max="100"
                  step="1"
                  value={scale}
                  onChange={(event) => {
                    setScale(parseInt(event.target.value));
                  }}
                ></RangeSelectorStyled>
              </Box>

              {canDelete && (
                <Box ml={{ sm: '400' }}>
                  <Button
                    appearance={ButtonAppearance.SECONDARY}
                    size={ButtonSize.SMALL}
                    onClick={(event) => {
                      event.preventDefault();
                      onDelete && onDelete();
                    }}
                  >
                    <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                      {deleting ? (
                        <Spinner></Spinner>
                      ) : (
                        <Icon icon="trash-alt" size={IconSize.MEDIUM} fillColor="blue.800" />
                      )}
                    </Box>

                    <Text size={'300'} color={'grey.1000'}>
                      <Trans> Delete </Trans>
                    </Text>
                  </Button>
                </Box>
              )}

              <Box ml={{ sm: '400' }}>
                <Button
                  appearance={ButtonAppearance.SECONDARY}
                  size={ButtonSize.SMALL}
                  onClick={(event) => {
                    event.preventDefault();
                    print();
                  }}
                  disabled={printing}
                >
                  <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                    {printing ? <Spinner></Spinner> : <Icon icon="print" size={IconSize.MEDIUM} fillColor="blue.800" />}
                  </Box>

                  <Text size={'300'} color={'grey.1000'}>
                    <Trans> Print</Trans>
                  </Text>
                </Button>
              </Box>
              <Box ml={{ sm: '400' }}>
                <Button
                  appearance={ButtonAppearance.SECONDARY}
                  size={ButtonSize.SMALL}
                  onClick={(event) => {
                    event.preventDefault();
                    window.open(url, 'popup_window', 'fullscreen=yes');
                  }}
                >
                  <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                    <Icon icon="expand" size={IconSize.MEDIUM} fillColor="blue.800" />
                  </Box>
                  <Text size={'300'} color={'grey.1000'}>
                    <Trans>Full Screen</Trans>
                  </Text>
                </Button>
              </Box>
            </DocumentNavStyled>
          )}
          <DocumentSectionStyled id="pdfDocument" ref={pdfDocumentRef}>
            <DocumentViewStyled>
              <Document
                file={file}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<Spinner size={SpinnerSize.LARGE}></Spinner>}
              >
                <Page
                  pageNumber={page}
                  renderAnnotationLayer={false}
                  scale={scale / 100}
                  key={page}
                  loading={<Spinner size={SpinnerSize.LARGE}></Spinner>}
                  onLoadSuccess={onPageLoadSuccess}
                />
              </Document>
            </DocumentViewStyled>
          </DocumentSectionStyled>
          <iframe ref={printFrameRef} style={{ display: 'none' }}></iframe>
        </>
      ) : (
        <EmptyDocumentContainerStyled>
          <EmptyDocumentContentStyled>
            <ViewDetailsSvg></ViewDetailsSvg>
            <Box mt={{ sm: '700' }}>
              <Text color="grey.600" size="400">
                <Trans> Select a document to preview.</Trans>
              </Text>
            </Box>
          </EmptyDocumentContentStyled>
        </EmptyDocumentContainerStyled>
      )}
    </>
  );
};
